// Local event generator from a one-sentence description (English or French), no AI: keywords, numbers, dates.
// Used by the API as a fallback when Claude is unavailable (no key, outage). The output then goes through
// normalizeEvent just like the AI's. Players may type either language, so every pattern lists both.
import { EVENT_CATS, JERSEYS, normalizeEvent, type CatId, type EventDef, type JerseyObjective, type Normalized, type StageDef, type StageMode } from './events.js';

const NUM: Record<string, number> = {
  un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6, sept: 7, huit: 8, neuf: 9, dix: 10, onze: 11, douze: 12, treize: 13, quatorze: 14, quinze: 15, seize: 16, vingt: 20, 'vingt et un': 21,
  one: 1, two: 2, three: 3, four: 4, five: 5, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, twenty: 20, 'twenty one': 21,
};
const DAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const DAYS_EN = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const MONTHS_EN = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
const MONTH_RE = [...MONTHS, 'fevrier', 'aout', 'decembre', ...MONTHS_EN].join('|');

const norm = (s: string) => s.toLowerCase().replace(/[’']/g, ' ').replace(/\s+/g, ' ');
const numRe = `(\\d+|${Object.keys(NUM).sort((a, b) => b.length - a.length).join('|')})`;
const toNum = (s: string) => (/^\d+$/.test(s) ? +s : NUM[s] ?? NaN);

/** Number in front of a word: "5 stages", "three laps", "5 étapes", "trois tours". */
function countOf(t: string, word: string): number | null {
  const m = new RegExp(`\\b${numRe}[\\s-]+${word}`).exec(t);
  return m ? toNum(m[1]) : null;
}

interface Theme { mode: StageMode; track: string; laps: number; name: string; extra?: Partial<StageDef> }
const THEMES: [RegExp, Theme][] = [
  [/montagne|col\b|cols\b|grimp|sommet|nuages|alpe|reine|mountain|climb|summit|cloud|\bpass\b|queen/, { mode: 'race', track: 'nuages', laps: 2, name: 'Mountain stage' }],
  [/par équipes?|par equipes?|team time trial|team trial|\bttt\b|équipes? contre|team against/, { mode: 'ttt', track: 'mur', laps: 1, name: 'Team time trial' }],
  // an individual time trial: the team one is matched above and must not count twice
  [/chrono(?! par [ée]quipes?)|contre[- ]la[- ]montre(?! par [ée]quipes?)|clm\b|(?<!team )time trial|against the clock|\btt\b/, { mode: 'tt', track: 'collines', laps: 1, name: 'Time trial' }],
  [/élimin|elimin/, { mode: 'elim', track: 'mur', laps: 1, name: 'Elimination' }],
  [/mur\b|puncheur|côte|cote\b|ardenn|flandr|pavé|\bwalls?\b|cobble|flanders|ardennes/, { mode: 'race', track: 'mur', laps: 4, name: 'Walls stage' }],
  [/sprint|plat|critérium|criterium|ville|boulevard|champs|flat|city|downtown/, { mode: 'race', track: 'crit', laps: 4, name: 'Flat stage', extra: { sprints: [0.5] } }],
  [/colline|vallon|accident|hill|rolling/, { mode: 'race', track: 'collines', laps: 3, name: 'Rolling stage' }],
  [/sortie|groupe|balade|récup|recup|group ride|social|easy ride|recovery ride/, { mode: 'free', track: 'collines', laps: 3, name: 'Group ride' }],
];
const ROTATION: Theme[] = [
  { mode: 'race', track: 'crit', laps: 4, name: 'Flat stage', extra: { sprints: [0.5] } },
  { mode: 'race', track: 'collines', laps: 3, name: 'Rolling stage' },
  { mode: 'tt', track: 'mur', laps: 2, name: 'Time trial' },
  { mode: 'race', track: 'nuages', laps: 2, name: 'Mountain stage' },
  { mode: 'race', track: 'mur', laps: 4, name: 'Walls stage' },
  { mode: 'elim', track: 'collines', laps: 1, name: 'Elimination' },
];

/** Time of day: "19h", "18h30", "7pm", "6:30 pm", "18:30". Returns [hours, minutes] or null. */
function timeOf(t: string): [number, number] | null {
  const fr = /(?:à|a|vers|dès|des)?\s*(\d{1,2})\s*h\s*(\d{2})?\b/.exec(t);
  if (fr) return [+fr[1], fr[2] ? +fr[2] : 0];
  const ampm = /\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/.exec(t);
  if (ampm) { const h = +ampm[1]; if (h < 1 || h > 12) return [99, 0]; return [(h % 12) + (ampm[3] === 'pm' ? 12 : 0), ampm[2] ? +ampm[2] : 0]; }
  const clock = /\b([01]?\d|2[0-3]):([0-5]\d)\b/.exec(t);
  return clock ? [+clock[1], +clock[2]] : null;
}

/**
 * Start date and time: "tomorrow 7pm", "saturday at 6:30 pm", "on november 12 at 8pm", "12 november", "12/11",
 * "demain 19h", "samedi à 18h30", "le 12 novembre à 20h", "le 12/11".
 */
export function parseWhen(t: string, now: number): number | null {
  const d = new Date(now);
  let day: Date | null = null;
  if (/apr[eè]s-demain|day after tomorrow/.test(t)) { day = new Date(d); day.setDate(d.getDate() + 2); }
  else if (/demain|tomorrow/.test(t)) { day = new Date(d); day.setDate(d.getDate() + 1); }
  else if (/aujourd|ce soir|ce midi|tout à l heure|today|tonight|this evening|this noon|later on/.test(t)) day = new Date(d);
  const setDate = (mo: number, dd: number) => {
    if (mo >= 0 && mo < 12 && dd >= 1 && dd <= 31) { day = new Date(d.getFullYear(), mo, dd); if (day.getTime() < now - 86400000) day.setFullYear(day.getFullYear() + 1); }
  };
  const monthIndex = (s: string) => {
    const fr = MONTHS.indexOf(s.replace('fevrier', 'février').replace('aout', 'août').replace('decembre', 'décembre'));
    return fr >= 0 ? fr : MONTHS_EN.indexOf(s);
  };
  // "12 novembre", "12/11", "12 november", "12th of november"
  const dm = new RegExp(`(?:le\\s+)?\\b(\\d{1,2})(?:st|nd|rd|th)?\\s*(?:\\/|\\s|\\s+of\\s+)\\s*(\\d{1,2}|${MONTH_RE})\\b`).exec(t);
  // "november 12", "november 12th"
  const md = new RegExp(`\\b(${MONTHS_EN.join('|')})\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`).exec(t);
  if (!day && dm) setDate(/^\d+$/.test(dm[2]) ? +dm[2] - 1 : monthIndex(dm[2]), +dm[1]);
  else if (!day && md) setDate(monthIndex(md[1]), +md[2]);
  if (!day) {
    const wd = DAYS.findIndex((x, i) => new RegExp(`\\b(?:${x}|${DAYS_EN[i]})\\b`).test(t));
    if (wd >= 0) { day = new Date(d); day.setDate(d.getDate() + ((wd - d.getDay() + 7) % 7 || 7)); }
  }
  const hm = timeOf(t);
  if (!day && !hm) return null;
  day = day || new Date(d);
  const h = hm ? hm[0] : /midi|noon|midday/.test(t) ? 12 : 19, m = hm ? hm[1] : 0;
  if (h > 23 || m > 59) return null;
  day.setHours(h, m, 0, 0);
  const dated = Boolean(dm || md) || /demain|tomorrow/.test(t);
  const weekday = /(lundi|mardi|mercredi|jeudi|vendredi|samedi|dimanche|monday|tuesday|wednesday|thursday|friday|saturday|sunday)/.test(t);
  if (day.getTime() < now && !dated) day.setDate(day.getDate() + (hm && !weekday ? 1 : 7));
  return day.getTime();
}

const CAT_WORD = 'cat(?:[ée]gories?|egor(?:y|ies))';
function catsOf(t: string): CatId[] | null {
  if (/tous niveaux|ouvert|tout le monde|débutants? et|pour tous|all levels|open to all|everyone|beginners and|for all/.test(t)) return ['A', 'B', 'C', 'D', 'E'];
  // score ranges ("300 to 450 points", "under 300 pts", "600 points and above"): every category the range touches
  const PTS = '(?:points?|pts)\\b';
  const touching = (lo: number, hi: number) => (['A', 'B', 'C', 'D'] as CatId[]).filter(c => EVENT_CATS[c].minScore <= hi && EVENT_CATS[c].maxScore > lo).reverse();
  const between = new RegExp(`(\\d{1,4})\\s*(?:à|a|-|–|to)\\s*(\\d{1,4})\\s*${PTS}`).exec(t);
  if (between) { const a = +between[1], b = +between[2]; return touching(Math.min(a, b), Math.max(a, b)); }
  const under = new RegExp(`(?:moins de|sous|under|below|less than)\\s*(\\d{1,4})\\s*${PTS}`).exec(t);
  if (under) return touching(0, +under[1] - 1);
  const above = new RegExp(`(\\d{1,4})\\s*${PTS}\\s*(?:et plus|ou plus|and above|and more|or more|\\+)`).exec(t);
  if (above) return touching(+above[1], Infinity);
  const range = new RegExp(`${CAT_WORD}\\s+([a-e])\\s*(?:à|a|-|au|to|through|thru)\\s*([a-e])\\b`).exec(t);
  if (range) { const ids: CatId[] = ['A', 'B', 'C', 'D', 'E'], i = ids.indexOf(range[1].toUpperCase() as CatId), j = ids.indexOf(range[2].toUpperCase() as CatId); return ids.slice(Math.min(i, j), Math.max(i, j) + 1); }
  const list = new RegExp(`${CAT_WORD}\\s+((?:[a-e](?:\\s*(?:,|et|and|\\/)\\s*)?)+)\\b`).exec(t);
  if (list) return [...new Set(list[1].replace(/\b(?:et|and)\b/g, ' ').toUpperCase().match(/[A-E]/g) as CatId[])];
  if (/débutant|debutant|découverte|facile|beginner|discovery|easy\b/.test(t)) return ['D', 'E'];
  if (/élite|elite|costaud|pros?\b|strong riders/.test(t)) return ['A', 'B'];
  return null;
}

function jerseysOf(t: string, tour: boolean): JerseyObjective[] {
  const want: JerseyObjective[] = [];
  const add = (o: JerseyObjective, re: RegExp) => { if (re.test(t)) want.push(o); };
  add('gc', /jaune|général|general|leader|yellow/);
  add('points', /vert|points|green/);
  add('mountain', /pois|grimpeur|montagne|polka|climber|mountain/);
  add('rookie', /blanc|jeune|néo|neo|débutant|white|young|rookie|beginner/);
  add('sprint', /rouge|sprints? intermédiaires?|\bred\b|intermediate sprints?/);
  add('combative', /combati|agressi|objets|touches|aggressive|items|hits/);
  add('wins', /arc-en-ciel|arc en ciel|victoires|rainbow|wins/);
  if (/maillots?|jerseys?|tour de france|façon tour|comme le tour|tour style|like the tour/.test(t) && want.length < 2 && tour) return ['gc', 'points', 'mountain', 'rookie'];
  if (tour && !want.includes('gc')) want.unshift('gc');
  return [...new Set(want)];
}

/** Event draft from a free-form description. */
export function draftEvent(prompt: string, now = Date.now()): Normalized {
  const t = norm(prompt);
  const nStages = countOf(t, '(?:étapes?|etapes?|manches?|jours?|courses?|stages?|rounds?|days?|races?)');
  const tour = /\btour\b|étapes|etapes|manches|championnat|coupe|triptyque|boucle|stages|rounds|championship|\bcup\b|triptych/.test(t) || (nStages ?? 1) > 1;
  const n = Math.max(1, Math.min(21, nStages ?? (tour ? 5 : 1)));
  const lapsAsked = countOf(t, '(?:tours?|laps?)');
  const found = THEMES.filter(([re]) => re.test(t)).map(([, th]) => th);
  const stages: StageDef[] = [];
  for (let i = 0; i < n; i++) {
    const th = !tour ? found[0] || ROTATION[1] : found.length && n <= found.length ? found[i] : found.length && i < found.length ? found[i] : ROTATION[(i + (found.length ? 1 : 0)) % ROTATION.length];
    const laps = lapsAsked && !tour ? lapsAsked : th.laps;
    stages.push({ name: th.name, mode: th.mode, track: th.track, laps, sprints: [], koms: true, ...(th.extra || {}) });
  }
  // a tour happily ends with a flat stage if nothing is imposed
  if (tour && n >= 4 && !found.length) stages[n - 1] = { name: 'City finale', mode: 'race', track: 'crit', laps: 5, sprints: [0.4, 0.8], koms: false };
  if (/sans objets?|pas d objets?|à la loyale|sans power|no items?|without items?|items off/.test(t)) for (const s of stages) s.items = false;
  const named = /(?:appel[ée]e?|nomm[ée]e?|intitul[ée]e?|nom|called|named|titled|name)\s*[:«"]?\s*([^,.»"]{3,50})/.exec(prompt);
  const theme = found[0];
  const name = named ? named[1].trim() : tour ? (theme?.track === 'nuages' ? 'Summits Tour' : theme?.track === 'crit' ? 'Sprinters’ Tour' : theme?.track === 'mur' ? 'Walls Tour' : 'Tour de Gruvetto') : theme ? theme.name.replace('stage', 'race') : 'Today’s race';
  const cats = catsOf(t);
  const pts = /aux points|classement par points|championnat|façon f1|comme en f1|by points|points classification|championship|f1 style|like f1/.test(t);
  const raw: Partial<EventDef> & Record<string, unknown> = {
    kind: tour ? 'tour' : 'event', name: name.charAt(0).toUpperCase() + name.slice(1), desc: prompt.trim().slice(0, 300),
    emoji: tour ? '🏁' : theme?.mode === 'elim' ? '❌' : theme?.mode === 'tt' || theme?.mode === 'ttt' ? '⏱️' : '🚴',
    startAt: parseWhen(t, now), categories: cats ?? (tour ? ['A', 'B', 'C', 'D', 'E'] : ['A', 'B', 'C', 'D']),
    enforce: !/sans contr[oô]le|libre choix|no control|free choice/.test(t), stages, jerseys: jerseysOf(t, tour) as never,
    scoring: { gc: pts ? 'points' : 'time', bonuses: !/sans bonif|no bonus|without bonus/.test(t) }, bots: /peloton|nombreux|gros|large field|big field|big bunch|many riders/.test(t) ? 11 : 7, prompt,
  };
  const out = normalizeEvent(raw, { now });
  out.warnings.unshift('Generated locally, without AI: check and tweak the stages.');
  return out;
}

/** Simple natural-language tweaks, no AI: "longer", "add a mountain stage", "remove stage 2"… (English or French) */
export function refineEvent(def: EventDef, instruction: string, now = Date.now()): Normalized {
  const t = norm(instruction), d = structuredClone(def) as EventDef & Record<string, unknown>;
  const notes: string[] = [];
  const which = (() => { const m = new RegExp(`(?:étape|etape|manche|stage|round)\\s+${numRe}`).exec(t); return m ? toNum(m[1]) - 1 : null; })();
  const targets = which != null && d.stages[which] ? [d.stages[which]] : d.stages;
  if (/plus (?:long|dur)|allonge|rallonge|longer|harder|lengthen|extend/.test(t)) for (const s of targets) s.laps++;
  if (/plus court|raccourci|moins long|plus facile|shorter|shorten|easier|less long/.test(t)) for (const s of targets) s.laps = Math.max(1, s.laps - 1);
  if (/supprime|enlève|enleve|retire|remove|delete|drop|take out/.test(t) && which != null && d.stages.length > 1) { d.stages.splice(which, 1); notes.push(`Stage ${which + 1} removed.`); }
  // French puts the theme after the noun ("ajoute une étape de montagne"), English before it ("add a mountain stage")
  const add = /(?:ajoute|add)\s+(?:une\s+|a\s+|an\s+)?(?:(?:étape|etape|manche|stage|round)\s*(.*)|(.+?)\s+(?:stage|round)\b)/.exec(t);
  if (add) {
    const th = THEMES.find(([re]) => re.test(add[1] ?? add[2] ?? ''))?.[1] || ROTATION[d.stages.length % ROTATION.length];
    d.stages.push({ name: th.name, mode: th.mode, track: th.track, laps: th.laps, sprints: [], koms: true, ...(th.extra || {}) });
  }
  if (/sans objets?|no items?|without items?|items off/.test(t)) for (const s of targets) s.items = false;
  if (/avec (?:les )?objets?|with items?|items on/.test(t)) for (const s of targets) delete s.items;
  const cats = catsOf(t);
  if (cats) d.categories = cats;
  for (const o of Object.keys(JERSEYS) as JerseyObjective[]) {
    const words: Record<JerseyObjective, RegExp> = { gc: /jaune|yellow/, points: /vert|green/, mountain: /pois|polka/, rookie: /blanc|white/, sprint: /rouge|\bred\b/, combative: /combati/, wins: /arc-en-ciel|arc en ciel|rainbow/ };
    if (!words[o].test(t)) continue;
    if (/sans|enlève|enleve|retire|supprime|without|remove|delete|drop/.test(t)) d.jerseys = d.jerseys.filter(j => j.objective !== o);
    else if (!d.jerseys.some(j => j.objective === o)) d.jerseys.push({ id: o, objective: o, ...JERSEYS[o] });
  }
  const when = parseWhen(t, now);
  if (when) d.startAt = when;
  if (/aux points|by points/.test(t)) d.scoring.gc = 'points';
  if (/au temps|by time/.test(t)) d.scoring.gc = 'time';
  const named = /(?:appelle|renomme|nomme|rename|call|name)[- ](?:le|la|l |it|the event)?\s*(?:en\s+|to\s+)?[«"]?([^»"]{3,50})/.exec(instruction);
  if (named) d.name = named[1].trim();
  const out = normalizeEvent(d, { now });
  out.warnings.unshift(...notes);
  if (JSON.stringify(out.def) === JSON.stringify(normalizeEvent(def, { now }).def)) out.warnings.unshift('Tweak not understood by the local generator: edit the stages by hand or connect the AI.');
  return out;
}

export const catLabel = (c: CatId) => EVENT_CATS[c].name;
